import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  readListOrigin,
  readPreviousPath,
  rememberListOrigin,
  samePath,
} from "../../helpers/functions/listOrigin";

// "/customer-single/932/jobs" and "/customer-single/932/assets" are the same
// record; "/single-job/59378/Notes" and "/single-job/59378" likewise. Two
// segments is enough for every detail route in this app.
const recordKeyOf = (pathname) =>
  "/" +
  String(pathname || "")
    .split("/")
    .filter(Boolean)
    .slice(0, 2)
    .join("/");

/**
 * Where a detail page's "← Back to X" link should point (Task 24795).
 *
 * The destination used to be a constant on each page, so /single-job/59378
 * always read "Back to Jobs List" even when it had been opened from a
 * customer's Jobs tab. This resolves it, in priority order:
 *
 *   1. `location.state.backTo` — an individual <Link> naming its own way back.
 *   2. The list that registered itself via useRegisterListOrigin / PageShell,
 *      but ONLY when that list is the page we actually came from.
 *   3. `defaultBackTo` — the page's existing hardcoded route. Still correct for
 *      a link pasted into a fresh tab, an email link or a search result, which
 *      is exactly why Task 24770 refused history.go(-1) in the first place.
 *
 * TWO THINGS MAKE THIS FIDDLIER THAN IT LOOKS, both caused by detail pages
 * syncing their tab to the URL with raw `window.history.pushState(null, …)`:
 *
 *   - That call writes `state: null` AND runs on mount, so `location.state` is
 *     wiped almost immediately. It has to be read on the FIRST render.
 *   - Every page is `lazy()`, so on a cold chunk the router renders a Suspense
 *     fallback and AuthProtected's effect fires BEFORE the real page renders.
 *     Anything resolved from "what rendered last" is therefore already the
 *     detail page itself by the time it asks. `readPreviousPath()` is written
 *     once per navigation and then holds still, which is why it is the previous
 *     path and not the last one.
 *
 * Hence the ref: the dynamic half is resolved once and held, while the
 * `defaultBackTo` fallback is applied live — some pages compute their default
 * only after the record loads (see CustomerAssetDetails), so it must not be
 * frozen on the first render.
 *
 * @param {{to: string, label: string}} [defaultBackTo] the page's own fallback
 * @returns {{to: string, label: string}|undefined}
 */

export const useBackTo = (defaultBackTo) => {
  const location = useLocation();
  const resolved = useRef({ key: null, dynamic: null });

  // Keyed on the RECORD, not the full path: a detail page rewrites its own URL
  // as you move between tabs, and re-resolving on every tab change would throw
  // away the answer worked out when the page was opened. /customer-single/932/
  // jobs and /customer-single/932/assets are the same record and keep the same
  // way back.
  const key = recordKeyOf(location?.pathname || "");

  if (resolved.current.key !== key) {
    const explicit = location?.state?.backTo || null;

    const origin = readListOrigin();
    // Two conditions. It has to be the page we actually came from, and it must
    // not be this very record - the customer's own Jobs tab registers itself as
    // a list, and the customer page must not offer "back" to a tab of itself.
    const cameFromThatList =
      !!origin &&
      samePath(origin.to, readPreviousPath()) &&
      recordKeyOf(String(origin.to).split("?")[0]) !== key;

    resolved.current = {
      key,
      dynamic: explicit || (cameFromThatList ? origin : null),
    };
  }

  return resolved.current.dynamic || defaultBackTo || undefined;
};

/**
 * A list announcing itself as somewhere worth going back to (Task 24795).
 *
 * PageShell calls this for every ordinary list page off its own `title`, so
 * nothing had to be wired page by page. Call it directly only for a list that
 * lives inside a record's tab and therefore has no PageShell of its own — the
 * customer's Jobs tab being the case that prompted all this.
 *
 * @param {Object} args
 * @param {string} args.label  what the back link should read, e.g. "Back to Jobs"
 * @param {string} [args.to]   route to return to; defaults to the current URL
 * @param {boolean} [args.enabled] skip registration until the data it needs exists
 */
export const useRegisterListOrigin = ({ label, to, enabled = true }) => {
  const location = useLocation();
  const path = `${location?.pathname || ""}${location?.search || ""}`;
  const target = to || path;

  useEffect(() => {
    if (!enabled || !label || !target) return;
    rememberListOrigin({ to: target, label });
  }, [enabled, label, target]);
};

export default useBackTo;
